/**
 * Offline-first sync (SY-01.3). Every change the player makes is applied locally at once
 * (the prediction) and queued as a SyncOp; POST /v1/sync sends the queue in order, in
 * batches, and answers with the authoritative SnapshotR1. When nothing is left to send the
 * snapshot replaces every server-owned field: the server wins, there is no local ledger.
 */
import { BOOSTERS } from '../config/content.js';
import { HttpError, NetworkError, type ApiClient } from './api.js';
import type { Snapshot, SyncResult } from './dto.js';
import { wire } from './ops.js';
import type { OutboxOp, PlayerState, Style } from './state.js';
import type { PlayerStore } from './store.js';

export type SyncStatus = 'disabled' | 'idle' | 'syncing' | 'offline' | 'synced';

export interface SyncRun {
  status: SyncStatus;
  sent: number;
  rejected: number;
}

/** Ops per request (the API accepts up to 100). */
export const BATCH = 50;

/** Server wins: overwrite everything the snapshot covers. */
export function reconcile(s: PlayerState, snap: Snapshot, now: number): void {
  s.coins = snap.balances.coins;
  s.gems = snap.balances.gems;
  s.stars = snap.balances.stars;
  s.xp = snap.player.xp;
  s.level = snap.player.level;
  s.shopName = snap.player.shopName;
  // boosters used in the round being played are not debited on the server yet (state.ts roundBoosters)
  for (const b of BOOSTERS) s.inventory[b] = Math.max(0, (snap.inventory[b] ?? 0) - (s.roundBoosters?.[b] ?? 0));
  s.levels = Object.fromEntries(
    Object.entries(snap.progress.levels).map(([id, lv]) => [id, { bestStars: lv.bestStars, bestTaps: lv.bestTaps, plays: lv.plays }]),
  );
  s.shops = Object.fromEntries(
    Object.entries(snap.progress.shops).map(([id, sh]) => [id, { upgrades: { ...(sh.upgrades as Record<string, Style>) }, ...(sh.decor ? { decor: { ...sh.decor } } : {}) }]),
  );
  s.flags.freeRescueUsed = snap.progress.freeRescueUsed;
  const m = snap.meta;
  if (m) {
    s.daily = { lastClaimDay: m.dailyGift.lastClaimDay, cycleDay: m.dailyGift.cycleDay };
    s.streak = { ...s.streak, count: m.streak.count, lastDay: m.streak.lastDay };
    s.lastCollectAt = m.offline.lastCollectAt;
    s.rareItems = { ...m.rareItems };
    s.stickers = { ...m.stickers };
    if (m.decor) s.decor = { ...m.decor };
    s.payer = m.payer;
    s.coppa = m.coppa;
    // SY-02: only the parent's emailed link verifies or revokes consent (accounts/parental.ts), so
    // the server's answer is the one that counts — on this device and on the child's other ones.
    // A server before the parental gate sends nothing, and the local flag stays.
    if (typeof m.parentVerified === 'boolean') s.parentVerified = m.parentVerified;
    s.consentVersion = m.consentVersion;
    s.linked = [...m.linked];
    if (m.costume) s.costume = m.costume; // MT-09.1: the worn costume dresses Pip on the map, in rounds and on share cards
    if (m.skin) s.skin = m.skin; // and his look (a server before migration 0010 sends none: the last one stays)
    // Clearer items and City outfits follow the account: a boolean the server holds wins, anything else leaves the local value.
    // The other settings are sent (services/ops.ts settingsBody) but not restored here: they need side effects (main.ts changeSettings).
    if (typeof m.settings.clearerItems === 'boolean') s.settings.clearerItems = m.settings.clearerItems;
    if (typeof m.settings.cityOutfits === 'boolean') s.settings.cityOutfits = m.settings.cityOutfits;
  }
  if (s.auth) s.auth.playerId = snap.player.id;
  s.syncedAt = now;
}

export interface SyncHooks {
  /** the server refused an op for good (its local prediction is undone by the snapshot) */
  onRejected?: (op: OutboxOp, reason: string) => void;
  /** per-op answer (round rewards, gift day, offline coins…) */
  onResult?: (op: OutboxOp, r: SyncResult) => void;
  onStatus?: (s: SyncStatus) => void;
  onReconciled?: (snap: Snapshot) => void;
}

export class SyncService {
  private _status: SyncStatus = 'idle';
  private inflight: Promise<SyncRun> | null = null;
  private backoffMs = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private store: PlayerStore,
    private api: ApiClient,
    private now: () => number,
    private hooks: SyncHooks = {},
  ) {
    if (!api.enabled) this._status = 'disabled';
  }

  get status(): SyncStatus {
    return this._status;
  }

  get busy(): boolean {
    return this.inflight !== null;
  }

  private set(s: SyncStatus): void {
    if (s === this._status) return;
    this._status = s;
    this.hooks.onStatus?.(s);
  }

  enqueue(op: OutboxOp): void {
    this.store.update((s) => s.outbox.push(op));
    if (!op.held) void this.sync();
  }

  /** Let a held op go (e.g. the round after its win screen), optionally amending its body. */
  release(opId: string, amend?: (op: OutboxOp) => void): void {
    this.store.update((s) => {
      const o = s.outbox.find((x) => x.opId === opId);
      if (!o) return;
      amend?.(o);
      delete o.held;
    });
    void this.sync();
  }

  /** Boot: nothing is on screen any more to hold an op for. */
  releaseAll(): void {
    if (!this.store.state.outbox.some((o) => o.held)) return;
    this.store.update((s) => s.outbox.forEach((o) => delete o.held));
  }

  /** Single-flight: concurrent callers share the running sync. */
  sync(): Promise<SyncRun> {
    if (!this.api.enabled) return Promise.resolve({ status: 'disabled', sent: 0, rejected: 0 });
    this.inflight ??= this.run().finally(() => (this.inflight = null));
    return this.inflight;
  }

  /**
   * Snapshot refresh for features whose own calls changed server state without returning a
   * snapshot: flushes the outbox, then pulls a snapshot fetched after this call (server wins).
   */
  async refresh(): Promise<SyncRun> {
    if (this.inflight) await this.inflight.catch(() => undefined);
    return this.sync();
  }

  /**
   * A snapshot from a direct call (ads, store, links…). Adopted at once when nothing is
   * pending locally; otherwise the next sync (which is started) brings a newer one.
   */
  adopt(snap: Snapshot | null | undefined): void {
    if (!snap) return;
    if (this.store.state.outbox.length === 0 && !this.inflight) {
      this.store.update((s) => reconcile(s, snap, this.now()));
      this.hooks.onReconciled?.(snap);
    } else void this.sync();
  }

  /** Ops that may go now: everything before the first held op, in order. */
  private sendable(max = BATCH): OutboxOp[] {
    const out: OutboxOp[] = [];
    for (const o of this.store.state.outbox) {
      if (o.held) break;
      out.push(o);
      if (out.length >= max) break;
    }
    return out;
  }

  /** A batch the API refused as a whole (validation): isolate the bad op by sending one at a time. */
  private async sendBatch(batch: OutboxOp[]): Promise<Awaited<ReturnType<ApiClient['sync']>> | null> {
    try {
      return await this.api.sync(batch.map((o) => wire(o) as never));
    } catch (e) {
      if (!(e instanceof HttpError) || ![400, 413, 422].includes(e.status)) throw e;
      if (batch.length > 1) {
        this.single = true;
        return null;
      }
      const o = batch[0]!;
      this.hooks.onRejected?.(o, e.code);
      this.store.update((s) => (s.outbox = s.outbox.filter((x) => x.opId !== o.opId)));
      return null;
    }
  }

  private single = false;

  private async run(): Promise<SyncRun> {
    this.set('syncing');
    let sent = 0;
    let rejected = 0;
    try {
      let snapshot: Snapshot | null = null;
      for (;;) {
        const batch = this.sendable(this.single ? 1 : BATCH);
        if (batch.length) {
          const reply = await this.sendBatch(batch);
          if (!reply) continue;
          const byId = new Map(batch.map((o) => [o.opId, o]));
          for (const r of reply.results) {
            const o = byId.get(r.opId);
            if (!o) continue;
            if (r.status === 'rejected') {
              rejected++;
              this.hooks.onRejected?.(o, r.reason ?? 'rejected');
            } else sent++;
            this.hooks.onResult?.(o, r);
          }
          const done = new Set(reply.results.map((r) => r.opId));
          this.store.update((s) => (s.outbox = s.outbox.filter((o) => !done.has(o.opId))));
          snapshot = reply.snapshot;
          continue;
        }
        snapshot ??= await this.api.me();
        // an op queued while we awaited is not in the snapshot yet: send it first
        if (this.sendable().length) {
          snapshot = null;
          continue;
        }
        break;
      }
      // held ops (a round on its win screen) are predicted locally: keep the prediction
      if (this.store.state.outbox.length === 0) {
        const snap = snapshot;
        this.store.update((s) => reconcile(s, snap, this.now()));
        this.hooks.onReconciled?.(snap);
      }
      this.backoffMs = 0;
      this.single = false;
      this.set('synced');
      return { status: 'synced', sent, rejected };
    } catch (e) {
      if (!(e instanceof NetworkError) && !(e instanceof HttpError)) throw e;
      this.set('offline');
      this.scheduleRetry();
      return { status: 'offline', sent, rejected };
    }
  }

  private scheduleRetry(): void {
    this.backoffMs = Math.min(60_000, this.backoffMs ? this.backoffMs * 2 : 2_000);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.store.state.outbox.some((o) => !o.held)) void this.sync();
    }, this.backoffMs);
  }
}
